import errorHandler from '../handlers/ErrorHandler.js';

/**
 * NavigationService - Handles complex view routing and content loading
 * Manages product detail views, guidebook views, and navigation state
 * 
 * @class NavigationService
 */
class NavigationService {
    constructor(productViewModel, guideBookViewModel, productRailViewModel, translationFunction) {
        this.productViewModel = productViewModel;
        this.guideBookViewModel = guideBookViewModel;
        this.productRailViewModel = productRailViewModel;
        this.t = translationFunction;
    }

    /**
     * Handle product detail view navigation
     * @async
     * @param {string} viewSlug - Full slug including 'product/' prefix
     * @returns {Object} Product data or error state
     */
    async handleProductView(viewSlug) {
        const slug = viewSlug.replace('product/', '');
        
        try {
            await this.productViewModel.loadProductDetail(slug);
            const product = this.productViewModel.currentProduct();
            
            if (!product) {
                return {
                    success: false,
                    title: 'Product Not Found',
                    htmlContent: '<p>Sorry, this product could not be found.</p>',
                    logoSrc: 'images/EngryvLogo_small.png',
                    logoFavicon: 'images/EngryvLogo_small.png'
                };
            }

            // Update title
            const title = product.name;
            const docTitle = product.name;
            if (product.productType === 'mobile') {
                var logoSrc = 'images/EngryvLogo_mobile_small.png';
                var logoFavicon = 'images/EngryvLogo_mobile_small.png';
            } else {
                var logoSrc = 'images/EngryvLogo_small.png';
                var logoFavicon = 'images/EngryvLogo_small.png';
            }

            // Show product rail
            await this.productRailViewModel.loadRailProducts();
            this.productRailViewModel.setCurrentProduct(product.id);
            this.productRailViewModel.setGuideBookView(false);
            this.productRailViewModel.scrollRailToActive();

            return {
                success: true,
                title,
                docTitle,
                htmlContent: product.fullDescription,
                logoSrc,
                logoFavicon,
                viewType: 'productDetail'
            };
        } catch (error) {
            errorHandler.handle(error, { viewSlug, stage: 'handleProductView' }, 'error');
            return {
                success: false,
                title: 'Product Not Found',
                htmlContent: '<p>Sorry, this product could not be found.</p>',
                logoSrc: 'images/EngryvLogo_small.png',
                logoFavicon: 'images/EngryvLogo_small.png'
            };
        }
    }

    /**
     * Handle product guidebook view navigation
     * @async
     * @param {string} viewSlug - Full slug including '/guidebook' suffix
     * @returns {Object} Guidebook data or error state
     */
    async handleProductGuideBookView(viewSlug) {
        const slug = viewSlug.replace('product/', '').replace('/guidebook', '');
        
        try {
            // Reset guidebook state
            this.guideBookViewModel.clearCache();

            await this.productViewModel.loadProductDetail(slug);
            await this.guideBookViewModel.loadMetadata();
            
            const product = this.productViewModel.currentProduct();
            
            if (!product) {
                return {
                    success: false,
                    title: 'Product Not Found',
                    htmlContent: '<p>Sorry, this product could not be found.</p>',
                    logoSrc: 'images/EngryvLogo_small.png',
                    logoFavicon: 'images/EngryvLogo_small.png'
                };
            }

            // Set product slug for guidebook queries
            await this.guideBookViewModel.setProductSlug(slug);

            const title = product.name + ' - Guidebook';
            const docTitle = product.name + ' - Guidebook';
            if (product.productType === 'mobile') {
                var logoSrc = 'images/EngryvLogo_mobile_small.png';
                var logoFavicon = 'images/EngryvLogo_mobile_small.png';
            } else {
                var logoSrc = 'images/EngryvLogo_small.png';
                var logoFavicon = 'images/EngryvLogo_small.png';
            }

            // Show product rail for guidebook
            await this.productRailViewModel.loadRailProducts();
            this.productRailViewModel.setCurrentProduct(product.id);
            this.productRailViewModel.setGuideBookView(true);
            this.productRailViewModel.scrollRailToActive();

            return {
                success: true,
                title,
                docTitle,
                htmlContent: '',
                logoSrc,
                logoFavicon,
                viewType: 'productGuideBook',
                isGuideBook: true
            };
        } catch (error) {
            errorHandler.handle(error, { viewSlug, stage: 'handleProductGuideBookView' }, 'error');
            return {
                success: false,
                title: 'Product Not Found',
                htmlContent: '<p>Sorry, this product could not be found.</p>',
                logoSrc: 'images/EngryvLogo_small.png',
                logoFavicon: 'images/EngryvLogo_small.png'
            };
        }
    }

    /**
     * Wait for guidebook preloading to complete
     * @async
     * @returns {Promise<void>}
     */
    async waitForGuideBookPreloading() {
        // Give Knockout time to apply CSS bindings
        if (window.ko && window.ko.tasks && typeof window.ko.tasks.schedule === 'function') {
            await new Promise(resolve => window.ko.tasks.schedule(() => resolve()));
        } else {
            await new Promise(resolve => setTimeout(resolve, 0));
        }
        
        // Then wait for preloading to finish
        await new Promise(resolve => {
            const checkPreloading = () => {
                if (!this.guideBookViewModel.isPreloadingContent()) {
                    resolve();
                } else {
                    setTimeout(checkPreloading, 50);
                }
            };
            checkPreloading();
        });
    }

    /**
     * Hide all product-related UI (rail, etc)
     */
    hideProductUI() {
        this.productRailViewModel.hideRail();
        this.productRailViewModel.setGuideBookView(false);
    }
}

export default NavigationService;
